import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook } from "lucide-react";
import ObfuscatedEmail, { emailMailto, useEmailAddress } from "@/components/ObfuscatedEmail";
import logo from "@/assets/logo.webp";

const Footer = () => {
  const emailAddress = useEmailAddress();

  return (
    <footer className="bg-charcoal text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand */}
          <div className="space-y-3 sm:space-y-4 col-span-2 md:col-span-1">
            <img 
              src={logo} 
              srcSet={`/logo-160.webp 347w, ${logo} 1388w`}
              sizes="(max-width: 640px) 240px, 347px"
              alt="Lawnworks Of NWA Logo"
              width={347}
              height={40}
              loading="lazy"
              className="h-auto w-auto max-w-full max-h-16 sm:max-h-20"
            />

            <p className="text-primary-foreground/70 text-xs sm:text-sm leading-relaxed">
              Family-owned and operated for over 25 years. Professional landscape and irrigation design & installation services across Northwest Arkansas.
            </p>
            <p className="text-accent-light font-semibold text-xs sm:text-sm">
              100% Satisfaction Guarantee
            </p>
            <a
              href="https://www.facebook.com/lawnworksofnwa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-accent-light transition-colors text-sm"
            >
              <Facebook className="h-5 w-5" />
              <span>Follow us on Facebook</span>
            </a>
            <a
              href="https://maps.google.com/?cid=9961656373227213968"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-accent-light transition-colors text-sm"
            >
              <MapPin className="h-5 w-5" />
              <span>See our Google Business Profile</span>
            </a>
          </div>

          {/* Quick Links */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-heading text-base sm:text-lg font-bold text-accent-light">Quick Links</h3>
            <nav className="flex flex-col gap-2 text-sm">
              <Link to="/" className="text-primary-foreground/70 hover:text-accent-light transition-colors">Home</Link>
              <Link to="/about" className="text-primary-foreground/70 hover:text-accent-light transition-colors">About Us</Link>
              <Link to="/services" className="text-primary-foreground/70 hover:text-accent-light transition-colors">Services</Link>
              <Link to="/gallery" className="text-primary-foreground/70 hover:text-accent-light transition-colors">Gallery</Link>
              <Link to="/faq" className="text-primary-foreground/70 hover:text-accent-light transition-colors">FAQ</Link>
              <Link to="/contact" className="text-primary-foreground/70 hover:text-accent-light transition-colors">Contact</Link>
            </nav>
          </div>

          {/* Services */}
          <div className="space-y-3 sm:space-y-4 hidden sm:block">
            <h3 className="font-heading text-base sm:text-lg font-bold text-accent-light">Services</h3>
            <nav className="flex flex-col gap-2 text-sm">
              <span className="text-primary-foreground/70">Landscape Design</span>
              <span className="text-primary-foreground/70">Irrigation Installation</span>
              <span className="text-primary-foreground/70">Drainage Services</span>
              <span className="text-primary-foreground/70">Seasonal Color</span>
              <span className="text-primary-foreground/70">Property Clean-Up</span>
              <span className="text-primary-foreground/70">Landscape Rejuvenation</span>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-heading text-base sm:text-lg font-bold text-accent-light">Contact Us</h3>
            <div className="space-y-2 sm:space-y-3 text-sm">
              <a href="tel:+14794649398" className="flex items-center gap-2 sm:gap-3 text-primary-foreground/70 hover:text-accent-light transition-colors">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <span>479-464-9398</span>
              </a>
              <a href={emailMailto(emailAddress)} className="flex items-center gap-2 sm:gap-3 text-primary-foreground/70 hover:text-accent-light transition-colors break-all">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <ObfuscatedEmail className="truncate" />
              </a>
              <div className="flex items-start gap-2 sm:gap-3 text-primary-foreground/70">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Hindsville, AR 72738</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 text-primary-foreground/70">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <span>Monday to Friday 8am–5pm</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border/20 mt-8 sm:mt-12 pt-6 sm:pt-8 text-center">
          <p className="text-primary-foreground/50 text-xs sm:text-sm">
            © {new Date().getFullYear()} Lawnworks Of NWA. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
